import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {useInterval} from 'shells/utils/hooks/use-interval';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';
import type {
    GetEvents as GetEventsQuery,
    GetEventsVariables as GetEventsQueryVariables,
    EventFragmentSparse,
} from 'nutshell-graphql-types';

import GET_EVENTS from '../queries/get-events.graphql';

export const GET_EVENTS_LIMIT = 20;
const POLL_INTERVAL_MILLISECONDS = 300000; // 5 minutes

export const useGetTimelineEvents = (
    queryVariables: GetEventsQueryVariables
): {
    events: EventFragmentSparse[];
    refetchEvents: () => Promise<any>;
    error: string | null | undefined;
    isLoading: boolean;
    isFirstLoad: boolean;
    fetchMoreEvents: () => Promise<any>;
    hasNextPage: boolean;
    fetchMostRecentEvents: () => Promise<any>;
} => {
    const [initialStartCursor, setInitialStartCursor] = React.useState<string | null | undefined>(
        null
    );

    const {
        data,
        loading: isLoading,
        networkStatus,
        error,
        refetch: refetchEvents,
        fetchMore,
    } = useQuery<GetEventsQuery, GetEventsQueryVariables>(GET_EVENTS, {
        variables: {...queryVariables, first: GET_EVENTS_LIMIT},
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    // This shows first load and also loading with new filters
    const isFirstLoad = networkStatus === 1 || networkStatus === 2;
    const eventsData = data ? data.events : null;
    const pageInfo = eventsData ? eventsData.pageInfo : {};
    // @ts-expect-error
    const {startCursor} = pageInfo;

    React.useEffect(() => {
        if (isFirstLoad) {
            setInitialStartCursor(null);
        } else if (!initialStartCursor && !isLoading) {
            // If no cursor currently set, set the initial start cursor
            setInitialStartCursor(startCursor);
        }
    }, [initialStartCursor, startCursor, isLoading, isFirstLoad]);

    useInterval(() => {
        if (initialStartCursor) {
            fetchMostRecentEvents(initialStartCursor);
        }
    }, POLL_INTERVAL_MILLISECONDS);

    const events = React.useMemo(() => {
        return eventsData
            ? eventsData.edges.map((edge) => {
                  return {...edge.node, pinnedInfo: edge.pinnedInfo};
              })
            : [];
    }, [eventsData]);

    const fetchMoreEvents = () => {
        return fetchMore({
            variables: {
                first: GET_EVENTS_LIMIT,
                after: eventsData ? eventsData.pageInfo.endCursor : null,
                filters: queryVariables.filters,
            },
            // @ts-expect-error
            updateQuery: (
                prev: GetEventsQuery,
                newResults: {
                    fetchMoreResult: GetEventsQuery;
                }
            ) => {
                const oldEvents = prev && prev.events ? prev.events : {edges: []};
                const fetchedEvents = newResults.fetchMoreResult.events ?? {edges: []};

                const existingIds = new Set(oldEvents.edges.map((edge) => edge.node.id));
                const uniqueNewEdges = fetchedEvents.edges.filter(
                    (edge) => !existingIds.has(edge.node.id)
                );

                const newEvents = {
                    ...oldEvents,
                    pageInfo: fetchedEvents.pageInfo,
                    // @ts-expect-error
                    edges: oldEvents.edges.concat(uniqueNewEdges),
                } as const;

                return {events: newEvents};
            },
        });
    };

    // Called when polling - gets any new events and puts them at the top of the timeline
    const fetchMostRecentEvents = (beforeCursor?: string | null) => {
        if (!beforeCursor) {
            // no-op if no beforeCursor is passed
            return new Promise((resolve) => {
                // @ts-expect-error
                resolve();
            });
        }

        return fetchMore({
            variables: {
                first: 20,
                before: beforeCursor,
                filters: queryVariables.filters,
            },
            // @ts-expect-error
            updateQuery: (
                prev: GetEventsQuery,
                newResults: {
                    fetchMoreResult: GetEventsQuery;
                }
            ) => {
                const oldEvents = prev && prev.events ? prev.events : {edges: []};
                const mostRecentEvents = newResults.fetchMoreResult.events ?? {edges: []};
                const existingIds = new Set(oldEvents.edges.map((edge) => edge.node.id));
                const uniqueNewEdges = mostRecentEvents.edges.filter(
                    (edge) => !existingIds.has(edge.node.id)
                );

                const newEvents = {
                    ...oldEvents,
                    // Put new events at the top of the timeline
                    edges: uniqueNewEdges.concat(oldEvents.edges),
                } as const;

                // Update the startCursor if it exists if no new events
                // the start cursor will be null
                if (mostRecentEvents && mostRecentEvents.pageInfo.startCursor) {
                    setInitialStartCursor(mostRecentEvents.pageInfo.startCursor);
                }

                return {events: newEvents};
            },
        });
    };

    const hasNextPage = eventsData ? eventsData.pageInfo.hasNextPage : false;

    return {
        events,
        refetchEvents,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading,
        isFirstLoad,
        fetchMoreEvents,
        fetchMostRecentEvents: () => {
            return fetchMostRecentEvents(initialStartCursor);
        },
        hasNextPage,
    };
};
